/**
 * What Nexus retains, what was true when, and what this caller may see.
 *
 * Three properties make this screen different from a list of things.
 *
 * **Two clocks, not one.** `as_of` asks what was *applicable* then; `known_at`
 * asks what Nexus *held* then. A revision written in August effective from July
 * reaches an `as_of` in July and must not reach a `known_at` in July — that
 * would describe a decision nobody could have taken with what was on hand. Both
 * default to now, the answer says which instants it used, and the cursor pins
 * them, so page two reconstructs the same past as page one instead of drifting
 * forward while somebody reads.
 *
 * **A withheld record produces no row.** The answer carries a count of what
 * matched and the caller may not read. A row is a fact about existence at a
 * position, and "one regulatory memory exists about this repository" is itself
 * the disclosure the partition exists to prevent — so the count is rendered and
 * nothing else. Not a greyed row, not a locked card, not a placeholder.
 *
 * **A space is absent or it is there; it is never withheld.** Spaces are now
 * readable (`NXP-MEMORY-SPACE-READ-001`), and that read discloses differently
 * from the one above it. A record the caller may not read is counted, because
 * they already know the space it sits in. A space they may not reach cannot be:
 * its existence is the claim that the enterprise retains memory about a
 * subject, so a placeholder row would disclose one space each and a count would
 * disclose how many. The platform sends neither, and this screen invents
 * neither — no greyed row, no "and N more", no arithmetic over what a page
 * might have been.
 *
 * Which is what makes retaining and opening a space workflows somebody can
 * complete, and therefore what makes them offerable at all. The `space_id` a
 * retention needs is chosen from spaces the caller read; there is no field to
 * type one into, because an identifier a browser assembled is an identifier
 * nobody discovered.
 *
 * The two reads run on different terms, deliberately. The *index* is read on
 * arrival — it is bounded, it is how somebody finds the subject they came for,
 * and an operation whose target is invisible until you go looking is not
 * offerable. The *contents* are not: browsing everything a tenant retains
 * should be a thing somebody chose, not the side effect of opening a page.
 */

import { useCallback, useRef, useState } from "react";
import { api } from "../api/client";
import type {
  DataClassification,
  EpistemicState,
  MemoryPurpose,
  MemoryResult,
  MemoryRevision,
  MemorySpace,
  MemorySubject,
  MemoryView,
  Page,
  RetentionClass,
  RetrievedMemory,
} from "../api/types";
import { usePagedCollection, type PageOf } from "../api/useCollection";
import { useResource } from "../api/useResource";
import {
  Banner,
  Card,
  Classification,
  Fact,
  Facts,
  Identifier,
  LowerBound,
  Mono,
  More,
  Pill,
  when,
  type Tone,
} from "../components/primitives";
import { change, RequiredAuthority, Result, type Outcome } from "../components/governed";
import { Empty, Forbidden, Show } from "../components/states";
import { useSession } from "../session/SessionProvider";

const MEMORY = "/memory";
const SPACES = "/memory/spaces";

const SUBJECTS: MemorySubject[] = ["product", "application", "repository"];

const PURPOSES: MemoryPurpose[] = [
  "engineering",
  "quality",
  "regulatory",
  "commercial",
  "operations",
];

const RETENTION: RetentionClass[] = [
  "architectural_constraint",
  "accepted_pattern",
  "recurring_defect",
  "qualification_lesson",
  "decision_rationale",
  "domain_fact",
  "operating_convention",
];

/** Weakest to strongest, which is the order they mean something in. */
const STANDINGS: EpistemicState[] = [
  "observed",
  "extracted",
  "inferred",
  "recommended",
  "confirmed",
  "approved",
];

/** The two the platform refuses without evidence behind them. */
const NEEDS_EVIDENCE: EpistemicState[] = ["confirmed", "approved"];

const STANDING_TONE: Record<EpistemicState, Tone> = {
  observed: "quiet",
  extracted: "quiet",
  inferred: "wait",
  recommended: "wait",
  confirmed: "mind",
  approved: "ok",
};

const LIFECYCLE_TONE = { active: "ok", revoked: "bad", retired: "quiet" } as const;

const CLASSIFICATIONS: DataClassification[] = [
  "public",
  "internal",
  "confidential",
  "quality",
  "regulatory",
  "pii",
  "phi",
  "executive",
  "safety_restricted",
];

// ── what the caller asked ─────────────────────────────────────────────────

type Scope = "subject" | "all";

interface Question {
  scope: Scope;
  subject: MemorySubject;
  subjectId: string;
  purpose: MemoryPurpose | "";
  retentionClass: RetentionClass | "";
  accepting: EpistemicState[];
  asOf: string;
  knownAt: string;
}

const BLANK: Question = {
  scope: "subject",
  subject: "repository",
  subjectId: "",
  purpose: "",
  retentionClass: "",
  accepting: [],
  asOf: "",
  knownAt: "",
};

/** Exactly what the platform is asked, so a test can read it back. */
function paramsOf(question: Question): Record<string, string | number | undefined> {
  return {
    subject: question.scope === "subject" ? question.subject : undefined,
    subject_id: question.scope === "subject" ? question.subjectId : undefined,
    purpose: question.purpose === "" ? undefined : question.purpose,
    retention_class: question.retentionClass === "" ? undefined : question.retentionClass,
    accepting: question.accepting.length > 0 ? question.accepting.join(",") : undefined,
    as_of: question.asOf === "" ? undefined : new Date(question.asOf).toISOString(),
    known_at: question.knownAt === "" ? undefined : new Date(question.knownAt).toISOString(),
    limit: 50,
  };
}

/**
 * A memory answer, read as a page.
 *
 * `MemoryResult` is not a `Page`. It has no `has_more`, because the walk says
 * it is finished a different way: by returning no cursor. The query contract
 * is explicit that an absent cursor "means the walk is finished [and] never
 * ... that the scan budget ran out", and the implementation mints a token only
 * when there is more to read — so `next_cursor !== null` *is* the platform's
 * own answer to whether another page exists, and mapping it is a statement of
 * that fact rather than an inference from one.
 *
 * The mapping lives here, at the screen, because it is true of memory and of
 * nothing else. A shared primitive taught it would have learned something
 * about one endpoint.
 *
 * And a page can carry no items and a cursor at once. The cursor comes from
 * the last row *scanned*, not the last row shown, so a window whose visible
 * rows were all withheld returns nothing and the walk still continues — which
 * is deliberate, because otherwise memory this caller may read would be
 * stranded behind memory they may not. Emptiness is not the end.
 */
function asPage(answer: MemoryResult): MemoryPage {
  return {
    items: answer.items,
    next_cursor: answer.next_cursor,
    has_more: answer.next_cursor !== null,
    as_of: answer.as_of,
    known_at: answer.known_at,
    view: answer.view,
    withheld: answer.withheld,
  };
}

/** The shared page shape, with what this answer says about itself still on it. */
interface MemoryPage extends PageOf<RetrievedMemory> {
  as_of: string;
  known_at: string;
  view: MemoryView;
  withheld: number;
}

/** What one page disclosed about what it did not show. */
interface Disclosure {
  withheld: number;
  view: MemoryView;
  /**
   * The page came back showing nothing, and so disclosed nothing.
   *
   * Not the same as "withheld nothing". A page with no visible rows returns
   * `withheld: 0` and `view: complete` however many rows it actually withheld,
   * deliberately — "nothing is disclosed here, including that anything was
   * withheld", because from where a caller seeing nothing stands there is
   * nothing to be short of. Composed with a page that *did* show something,
   * that silence is a hole in the arithmetic, and this is what remembers it.
   */
  silent: boolean;
}

function Ask({ onAsk }: { onAsk: (question: Question) => void }) {
  const [question, setQuestion] = useState<Question>(BLANK);

  return (
    <Card title="What are you asking about?">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onAsk({ ...question });
        }}
      >
        <div className="nx-field" role="group" aria-label="Scope">
          <label htmlFor="scope-subject">Scope</label>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input
                id="scope-subject"
                type="radio"
                name="scope"
                checked={question.scope === "subject"}
                onChange={() => {
                  setQuestion({ ...question, scope: "subject" });
                }}
              />
              One subject
            </label>
            <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input
                id="scope-all"
                type="radio"
                name="scope"
                checked={question.scope === "all"}
                onChange={() => {
                  setQuestion({ ...question, scope: "all" });
                }}
              />
              Across all subjects
            </label>
          </div>
          <span className="nx-hint">
            Nothing is read until you ask. Reading everything a tenant retains is a decision, not
            what happens when a page opens.
          </span>
        </div>

        {question.scope === "subject" ? (
          <>
            <div className="nx-field">
              <label htmlFor="subject-type">Subject type</label>
              <select
                id="subject-type"
                className="nx-select"
                value={question.subject}
                onChange={(event) => {
                  setQuestion({ ...question, subject: event.target.value as MemorySubject });
                }}
              >
                {SUBJECTS.map((subject) => (
                  <option key={subject} value={subject}>
                    {subject}
                  </option>
                ))}
              </select>
              <span className="nx-hint">
                Products, applications and repositories. Memory about a person is a separate
                capability that does not exist yet.
              </span>
            </div>
            <div className="nx-field">
              <label htmlFor="subject-id">Subject</label>
              <input
                id="subject-id"
                className="nx-input"
                required
                value={question.subjectId}
                onChange={(event) => {
                  setQuestion({ ...question, subjectId: event.target.value });
                }}
              />
            </div>
          </>
        ) : null}

        <div className="nx-field">
          <label htmlFor="purpose">Purpose</label>
          <select
            id="purpose"
            className="nx-select"
            value={question.purpose}
            onChange={(event) => {
              setQuestion({ ...question, purpose: event.target.value as MemoryPurpose | "" });
            }}
          >
            <option value="">Any</option>
            {PURPOSES.map((purpose) => (
              <option key={purpose} value={purpose}>
                {purpose}
              </option>
            ))}
          </select>
        </div>

        <div className="nx-field">
          <label htmlFor="retention">Retention class</label>
          <select
            id="retention"
            className="nx-select"
            value={question.retentionClass}
            onChange={(event) => {
              setQuestion({
                ...question,
                retentionClass: event.target.value as RetentionClass | "",
              });
            }}
          >
            <option value="">Any</option>
            {RETENTION.map((retention) => (
              <option key={retention} value={retention}>
                {retention.replace(/_/g, " ")}
              </option>
            ))}
          </select>
        </div>

        <div className="nx-field">
          <label htmlFor="as-of">As things stood</label>
          <input
            id="as-of"
            className="nx-input"
            type="datetime-local"
            value={question.asOf}
            onChange={(event) => {
              setQuestion({ ...question, asOf: event.target.value });
            }}
          />
          <span className="nx-hint">What was applicable then. Leave empty for now.</span>
        </div>

        <div className="nx-field">
          <label htmlFor="known-at">As Nexus knew it</label>
          <input
            id="known-at"
            className="nx-input"
            type="datetime-local"
            value={question.knownAt}
            onChange={(event) => {
              setQuestion({ ...question, knownAt: event.target.value });
            }}
          />
          <span className="nx-hint">
            What Nexus held then. A different question — a correction written last week does not
            belong in a reconstruction of last year.
          </span>
        </div>

        <button type="submit" className="nx-button nx-button--primary">
          Ask
        </button>
      </form>
    </Card>
  );
}

// ── one revision, and what holds it up ────────────────────────────────────

function Revision({ revisionId }: { revisionId: string }) {
  const read = useCallback(
    (signal: AbortSignal) =>
      api.get<MemoryRevision>(`${MEMORY}/revisions/${revisionId}`, { signal }),
    [revisionId],
  );
  const { state, reload } = useResource<MemoryRevision>(read);

  return (
    <Show resource={state} what="this revision" onRetry={reload}>
      {(revision) => (
        <div style={{ display: "grid", gap: 12 }}>
          <p style={{ margin: 0 }}>{revision.statement}</p>
          <Facts>
            <Fact label="Standing">
              <Pill tone={STANDING_TONE[revision.epistemic_state]}>
                {revision.epistemic_state}
              </Pill>
            </Fact>
            <Fact label="Classification">
              <Classification value={revision.classification} />
            </Fact>
            <Fact label="Applicable">
              {when(revision.effective_from)}
              {revision.valid_until ? ` until ${when(revision.valid_until)}` : " · open-ended"}
            </Fact>
            <Fact label="Written">
              {when(revision.recorded_at)} by <Identifier value={revision.recorded_by} />
            </Fact>
            <Fact label="Reason">{revision.reason}</Fact>
            {revision.supersedes_revision_id ? (
              <Fact label="Supersedes">
                <Identifier value={revision.supersedes_revision_id} />
              </Fact>
            ) : null}
          </Facts>

          <div>
            <h3 style={{ fontSize: "var(--nx-text-base)", margin: "0 0 6px" }}>What holds it up</h3>
            {revision.evidence.length === 0 ? (
              <p className="nx-hint" style={{ margin: 0 }}>
                Nothing is cited. A confirmed or approved standing would have been refused without
                evidence; weaker standings may stand alone.
              </p>
            ) : (
              <ul className="nx-timeline">
                {revision.evidence.map((evidence) => (
                  <li key={evidence.evidence_id}>
                    <span>
                      <Mono>{evidence.source_ref}</Mono>
                      <span className="nx-hint" style={{ display: "block" }}>
                        {evidence.source_kind}
                        {evidence.source_system ? ` · ${evidence.source_system}` : ""} ·{" "}
                        {when(evidence.captured_at)}
                        {evidence.source_digest ? ` · ${evidence.source_digest}` : ""}
                      </span>
                      {evidence.note ? <span className="nx-hint">{evidence.note}</span> : null}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className="nx-hint" style={{ marginTop: 8 }}>
              A reference and a digest, never the material itself.
            </p>
          </div>
        </div>
      )}
    </Show>
  );
}

// ── acting on a record ────────────────────────────────────────────────────

function Revise({
  record,
  onChanged,
  report,
}: {
  record: RetrievedMemory;
  onChanged: () => void;
  report: (outcome: Outcome) => void;
}) {
  const session = useSession();
  const [form, setForm] = useState({
    statement: "",
    standing: record.epistemic_state,
    classification: record.classification,
    reason: "",
    sourceRef: "",
    sourceKind: "manual",
  });
  const [busy, setBusy] = useState(false);

  if (!session.may("memory.revise")) {
    return <RequiredAuthority capability="brain.memory.revise" what="Revising this memory" />;
  }

  const needsEvidence = NEEDS_EVIDENCE.includes(form.standing);

  return (
    <form
      style={{ marginTop: 12 }}
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        void change(
          (correlationId) =>
            api.post<MemoryRevision>(
              `${MEMORY}/${record.record_id}/revisions`,
              {
                // The revision this correction was written against. If the
                // memory moved on in between, the platform refuses rather than
                // accepting a claim about a statement nobody read.
                expected_current_revision_id: record.revision_id,
                statement: form.statement,
                epistemic_state: form.standing,
                classification: form.classification,
                evidence:
                  form.sourceRef === ""
                    ? []
                    : [
                        {
                          source_kind: form.sourceKind,
                          source_ref: form.sourceRef,
                          captured_at: new Date().toISOString(),
                        },
                      ],
                reason: form.reason,
              },
              { correlationId },
            ),
          "Revising this memory",
          "Revised. What it said before is still readable.",
        )
          .then((outcome) => {
            report(outcome);
            if (outcome.tone === "ok") {
              setForm({ ...form, statement: "", reason: "", sourceRef: "" });
              onChanged();
            }
          })
          .finally(() => {
            setBusy(false);
          });
      }}
    >
      <h3 style={{ fontSize: "var(--nx-text-base)", margin: "0 0 10px" }}>Revise</h3>
      <div className="nx-field">
        <label htmlFor="revise-statement">What it should say</label>
        <input
          id="revise-statement"
          className="nx-input"
          required
          value={form.statement}
          onChange={(event) => {
            setForm({ ...form, statement: event.target.value });
          }}
        />
        <span className="nx-hint">
          A revision supersedes; it does not overwrite. What was believed before stays readable.
        </span>
      </div>
      <div className="nx-field">
        <label htmlFor="revise-standing">Standing</label>
        <select
          id="revise-standing"
          className="nx-select"
          value={form.standing}
          onChange={(event) => {
            setForm({ ...form, standing: event.target.value as EpistemicState });
          }}
        >
          {STANDINGS.map((standing) => (
            <option key={standing} value={standing}>
              {standing}
            </option>
          ))}
        </select>
        {needsEvidence ? (
          <span className="nx-hint">
            A {form.standing} claim names what holds it up. Without evidence the platform refuses
            it.
          </span>
        ) : null}
      </div>
      <div className="nx-field">
        <label htmlFor="revise-classification">Classification</label>
        <select
          id="revise-classification"
          className="nx-select"
          value={form.classification}
          onChange={(event) => {
            setForm({ ...form, classification: event.target.value as DataClassification });
          }}
        >
          {CLASSIFICATIONS.map((value) => (
            <option key={value} value={value}>
              {value.replace(/_/g, " ")}
            </option>
          ))}
        </select>
        <span className="nx-hint">
          A record may not be more sensitive than the space holding it.
        </span>
      </div>
      <div className="nx-field">
        <label htmlFor="revise-evidence">Evidence</label>
        <input
          id="revise-evidence"
          className="nx-input"
          required={needsEvidence}
          value={form.sourceRef}
          onChange={(event) => {
            setForm({ ...form, sourceRef: event.target.value });
          }}
        />
        <span className="nx-hint">A reference, never the material itself.</span>
      </div>
      <div className="nx-field">
        <label htmlFor="revise-reason">Why</label>
        <input
          id="revise-reason"
          className="nx-input"
          required
          value={form.reason}
          onChange={(event) => {
            setForm({ ...form, reason: event.target.value });
          }}
        />
      </div>
      <button type="submit" className="nx-button nx-button--primary" disabled={busy}>
        Revise
      </button>
    </form>
  );
}

function Withdraw({
  record,
  onChanged,
  report,
}: {
  record: RetrievedMemory;
  onChanged: () => void;
  report: (outcome: Outcome) => void;
}) {
  const session = useSession();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  if (!session.may("memory.revoke")) {
    return (
      <RequiredAuthority
        capability="brain.memory.revoke"
        what="Revoking and retiring this memory"
      />
    );
  }

  const act = (what: "revocation" | "retirement") => {
    setBusy(what);
    void change(
      (correlationId) =>
        api.post(`${MEMORY}/${record.record_id}/${what}`, { reason }, { correlationId }),
      what === "revocation" ? "Revoking this memory" : "Retiring this memory",
      what === "revocation"
        ? "Revoked. Every revision stays readable."
        : "Retired. It is no longer maintained, and it is not withdrawn.",
    )
      .then((outcome) => {
        report(outcome);
        if (outcome.tone === "ok") {
          setReason("");
          onChanged();
        }
      })
      .finally(() => {
        setBusy(null);
      });
  };

  return (
    <div style={{ marginTop: 12 }}>
      <h3 style={{ fontSize: "var(--nx-text-base)", margin: "0 0 10px" }}>Withdraw</h3>
      <div className="nx-field">
        <label htmlFor="withdraw-reason">Why</label>
        <input
          id="withdraw-reason"
          className="nx-input"
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          className="nx-button"
          disabled={busy !== null || reason === ""}
          onClick={() => {
            act("revocation");
          }}
        >
          Revoke
        </button>
        <button
          type="button"
          className="nx-button"
          disabled={busy !== null || reason === ""}
          onClick={() => {
            act("retirement");
          }}
        >
          Retire
        </button>
      </div>
      <p className="nx-hint" style={{ marginTop: 8 }}>
        Revoking stops the enterprise standing behind it. Retiring stops maintaining it without
        withdrawing it. They are different acts, and the platform governs them under one
        authority on purpose: ending what the enterprise retains is a single decision.
      </p>
    </div>
  );
}

// ── where memory is kept ──────────────────────────────────────────────────

const SPACE_PAGE = 50;

/**
 * The spaces this caller may know exist.
 *
 * A single fixed collection, so it needs no `resetKey`: there is one index per
 * tenant and paging it is paging it. Read on arrival, unlike the query below,
 * because it is the index rather than the contents — bounded, and the only way
 * anybody finds the `space_id` a retention has to name.
 */
function useSpaces() {
  const readPage = useCallback(
    (cursor: string | null, signal: AbortSignal) =>
      api.get<Page<MemorySpace>>(SPACES, {
        params: { limit: SPACE_PAGE, cursor: cursor ?? undefined },
        signal,
      }),
    [],
  );
  return usePagedCollection<MemorySpace>(readPage);
}

function OpenSpace({ onOpened, report }: { onOpened: () => void; report: (outcome: Outcome) => void }) {
  const session = useSession();
  const [form, setForm] = useState({
    subject: "repository" as MemorySubject,
    subjectId: "",
    purpose: "engineering" as MemoryPurpose,
    ceiling: "internal" as DataClassification,
    owner: "",
  });
  const [busy, setBusy] = useState(false);

  if (!session.may("memory_space.open")) {
    return (
      <RequiredAuthority
        capability="brain.memory_space.open"
        what="Opening a memory space"
      />
    );
  }

  return (
    <form
      style={{ marginTop: 12 }}
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        void change(
          (correlationId) =>
            api.post<MemorySpace>(
              SPACES,
              {
                subject: form.subject,
                subject_id: form.subjectId,
                purpose: form.purpose,
                classification_ceiling: form.ceiling,
                owner: form.owner,
              },
              { correlationId },
            ),
          "Opening a memory space",
          "Opened. Memory may now be retained about this subject, for this purpose.",
        )
          .then((outcome) => {
            report(outcome);
            if (outcome.tone === "ok") {
              setForm({ ...form, subjectId: "", owner: "" });
              onOpened();
            }
          })
          .finally(() => {
            setBusy(false);
          });
      }}
    >
      <h3 style={{ fontSize: "var(--nx-text-base)", margin: "0 0 10px" }}>Open a space</h3>
      <p className="nx-hint" style={{ marginTop: 0 }}>
        Agreeing that the enterprise retains memory about a subject, for a purpose. A governance
        decision, which is why it is granted apart from writing memory — and why the subject has to
        already exist in the registry: memory about something Nexus cannot resolve is memory nobody
        can govern.
      </p>
      <div className="nx-field">
        <label htmlFor="open-subject">Kind of subject</label>
        <select
          id="open-subject"
          className="nx-select"
          value={form.subject}
          onChange={(event) => {
            setForm({ ...form, subject: event.target.value as MemorySubject });
          }}
        >
          {SUBJECTS.map((subject) => (
            <option key={subject} value={subject}>
              {subject}
            </option>
          ))}
        </select>
      </div>
      <div className="nx-field">
        <label htmlFor="open-subject-id">Subject to open memory about</label>
        <input
          id="open-subject-id"
          className="nx-input"
          required
          value={form.subjectId}
          onChange={(event) => {
            setForm({ ...form, subjectId: event.target.value });
          }}
        />
      </div>
      <div className="nx-field">
        <label htmlFor="open-purpose">What the memory is for</label>
        <select
          id="open-purpose"
          className="nx-select"
          value={form.purpose}
          onChange={(event) => {
            setForm({ ...form, purpose: event.target.value as MemoryPurpose });
          }}
        >
          {PURPOSES.map((purpose) => (
            <option key={purpose} value={purpose}>
              {purpose}
            </option>
          ))}
        </select>
        <span className="nx-hint">
          One open space per subject and purpose. Without that there are three competing containers
          for one repository and no rule saying which is authoritative.
        </span>
      </div>
      <div className="nx-field">
        <label htmlFor="open-ceiling">Classification ceiling</label>
        <select
          id="open-ceiling"
          className="nx-select"
          value={form.ceiling}
          onChange={(event) => {
            setForm({ ...form, ceiling: event.target.value as DataClassification });
          }}
        >
          {CLASSIFICATIONS.map((value) => (
            <option key={value} value={value}>
              {value.replace(/_/g, " ")}
            </option>
          ))}
        </select>
        <span className="nx-hint">
          The most sensitive thing this space may ever hold. A ceiling, never a grant — and it is
          also who may find the space at all, since a reader whose memory authority does not reach
          it is never told there is one.
        </span>
      </div>
      <div className="nx-field">
        <label htmlFor="open-owner">Owner</label>
        <input
          id="open-owner"
          className="nx-input"
          required
          value={form.owner}
          onChange={(event) => {
            setForm({ ...form, owner: event.target.value });
          }}
        />
      </div>
      <button type="submit" className="nx-button nx-button--primary" disabled={busy}>
        Open space
      </button>
    </form>
  );
}

function Retain({
  space,
  onKept,
  report,
}: {
  space: MemorySpace;
  onKept: () => void;
  report: (outcome: Outcome) => void;
}) {
  const session = useSession();
  const [form, setForm] = useState({
    retentionClass: "architectural_constraint" as RetentionClass,
    title: "",
    statement: "",
    standing: "observed" as EpistemicState,
    classification: "internal" as DataClassification,
    reason: "",
    sourceRef: "",
    sourceKind: "manual",
  });
  const [busy, setBusy] = useState(false);

  if (!session.may("memory.retain")) {
    return <RequiredAuthority capability="brain.memory.retain" what="Retaining memory here" />;
  }

  const needsEvidence = NEEDS_EVIDENCE.includes(form.standing);

  return (
    <form
      style={{ marginTop: 12 }}
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        void change(
          (correlationId) =>
            api.post(
              MEMORY,
              {
                // Read, never assembled. The only way this identifier reaches
                // here is a space this caller enumerated, which is the whole
                // reason retaining is offerable at all.
                space_id: space.space_id,
                retention_class: form.retentionClass,
                title: form.title,
                statement: form.statement,
                epistemic_state: form.standing,
                classification: form.classification,
                evidence:
                  form.sourceRef === ""
                    ? []
                    : [
                        {
                          source_kind: form.sourceKind,
                          source_ref: form.sourceRef,
                          captured_at: new Date().toISOString(),
                        },
                      ],
                reason: form.reason,
              },
              { correlationId },
            ),
          "Retaining this memory",
          "Retained. It applies from now, and every revision of it stays readable.",
        )
          .then((outcome) => {
            report(outcome);
            if (outcome.tone === "ok") {
              setForm({ ...form, title: "", statement: "", reason: "", sourceRef: "" });
              onKept();
            }
          })
          .finally(() => {
            setBusy(false);
          });
      }}
    >
      <h3 style={{ fontSize: "var(--nx-text-base)", margin: "0 0 10px" }}>
        Retain something here
      </h3>
      <Facts>
        <Fact label="Space">
          <Identifier value={space.space_id} />
        </Fact>
        <Fact label="About">
          {space.subject} <Identifier value={space.subject_id} />
        </Fact>
        <Fact label="For">{space.purpose}</Fact>
        <Fact label="Ceiling">
          <Classification value={space.classification_ceiling} />
        </Fact>
      </Facts>
      <div className="nx-field">
        <label htmlFor="retain-class">Kind of knowledge</label>
        <select
          id="retain-class"
          className="nx-select"
          value={form.retentionClass}
          onChange={(event) => {
            setForm({ ...form, retentionClass: event.target.value as RetentionClass });
          }}
        >
          {RETENTION.map((retention) => (
            <option key={retention} value={retention}>
              {retention.replace(/_/g, " ")}
            </option>
          ))}
        </select>
        <span className="nx-hint">
          A closed vocabulary. A branch, a build status or a deployment is none of these, and there
          is nowhere else to put them — which is what keeps memory from becoming operational state.
        </span>
      </div>
      <div className="nx-field">
        <label htmlFor="retain-title">What it is about</label>
        <input
          id="retain-title"
          className="nx-input"
          required
          value={form.title}
          onChange={(event) => {
            setForm({ ...form, title: event.target.value });
          }}
        />
      </div>
      <div className="nx-field">
        <label htmlFor="retain-statement">What it says</label>
        <input
          id="retain-statement"
          className="nx-input"
          required
          value={form.statement}
          onChange={(event) => {
            setForm({ ...form, statement: event.target.value });
          }}
        />
      </div>
      <div className="nx-field">
        <label htmlFor="retain-standing">How strongly it is held</label>
        <select
          id="retain-standing"
          className="nx-select"
          value={form.standing}
          onChange={(event) => {
            setForm({ ...form, standing: event.target.value as EpistemicState });
          }}
        >
          {STANDINGS.map((standing) => (
            <option key={standing} value={standing}>
              {standing}
            </option>
          ))}
        </select>
        {needsEvidence ? (
          <span className="nx-hint">
            A {form.standing} claim names what holds it up. Without evidence the platform refuses
            it.
          </span>
        ) : null}
      </div>
      <div className="nx-field">
        <label htmlFor="retain-classification">Sensitivity</label>
        <select
          id="retain-classification"
          className="nx-select"
          value={form.classification}
          onChange={(event) => {
            setForm({ ...form, classification: event.target.value as DataClassification });
          }}
        >
          {CLASSIFICATIONS.map((value) => (
            <option key={value} value={value}>
              {value.replace(/_/g, " ")}
            </option>
          ))}
        </select>
        <span className="nx-hint">
          Every classification is offered and the platform decides. This space is cleared to{" "}
          {space.classification_ceiling.replace(/_/g, " ")}, and a record above that is refused
          there — a browser that greyed the option out would be making the decision instead of
          describing it.
        </span>
      </div>
      <div className="nx-field">
        <label htmlFor="retain-evidence">Evidence reference</label>
        <input
          id="retain-evidence"
          className="nx-input"
          required={needsEvidence}
          value={form.sourceRef}
          onChange={(event) => {
            setForm({ ...form, sourceRef: event.target.value });
          }}
        />
        <span className="nx-hint">A reference, never the material itself.</span>
      </div>
      <div className="nx-field">
        <label htmlFor="retain-reason">Why it is worth keeping</label>
        <input
          id="retain-reason"
          className="nx-input"
          required
          value={form.reason}
          onChange={(event) => {
            setForm({ ...form, reason: event.target.value });
          }}
        />
      </div>
      <button type="submit" className="nx-button nx-button--primary" disabled={busy}>
        Retain
      </button>
    </form>
  );
}

/**
 * The index, and the two acts it makes completable.
 *
 * What is *not* here is as deliberate as what is. There is no count of spaces
 * this caller may not reach, and no row standing in for one, because either
 * would disclose what the ceiling exists to withhold — that the enterprise
 * retains memory about some subject. So an empty index says only that: nothing
 * this caller may know about. It does not say "nothing exists", and it does not
 * hint that something might.
 */
function Spaces({ report }: { report: (outcome: Outcome) => void }) {
  const session = useSession();
  const spaces = useSpaces();
  const [target, setTarget] = useState<string | null>(null);

  const chosen = spaces.items.find((space) => space.space_id === target) ?? null;

  return (
    <Card
      title="Where memory is kept"
      actions={
        <button type="button" className="nx-button" onClick={spaces.refresh}>
          Refresh
        </button>
      }
      bodyless
    >
      <Show resource={spaces.state} what="the spaces memory is kept in" onRetry={spaces.refresh}>
        {() =>
          spaces.items.length === 0 ? (
            <Empty
              title="No memory space you may see is open."
              hint="Spaces you are not cleared to know about are absent from this list rather than counted in it, so this says what you may know and nothing about what you may not."
            />
          ) : (
            <div className="nx-table-wrap">
              <table className="nx-table">
                <caption className="nx-visually-hidden">Memory spaces</caption>
                <thead>
                  <tr>
                    <th scope="col">About</th>
                    <th scope="col">For</th>
                    <th scope="col">Ceiling</th>
                    <th scope="col">Owner</th>
                    <th scope="col">State</th>
                    <th scope="col" />
                  </tr>
                </thead>
                <tbody>
                  {spaces.items.map((space) => (
                    <tr key={space.space_id}>
                      <td>
                        <span className="nx-table__primary">{space.subject}</span>
                        <span className="nx-table__secondary" style={{ display: "block" }}>
                          <Identifier value={space.subject_id} />
                        </span>
                      </td>
                      <td>{space.purpose}</td>
                      <td>
                        <Classification value={space.classification_ceiling} />
                      </td>
                      <td>{space.owner}</td>
                      <td>
                        <Pill tone={space.closed_at === null ? "ok" : "quiet"}>
                          {space.closed_at === null ? "open" : "closed"}
                        </Pill>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="nx-button"
                          aria-pressed={target === space.space_id}
                          onClick={() => {
                            setTarget(target === space.space_id ? null : space.space_id);
                          }}
                        >
                          {target === space.space_id ? "Chosen" : "Retain here"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </Show>

      <More
        collection={spaces}
        label="Show more spaces"
        hint="This is one page of the index, not all of it. Nexus returned a position to continue from."
        placement="bodyless"
      />

      <div className="nx-card__body">
        {chosen === null ? (
          <p className="nx-hint" style={{ margin: 0 }}>
            {session.may("memory.retain")
              ? "Choose a space to retain something in it. There is no field to type an identifier into: a space you did not read is a space nobody discovered."
              : null}
          </p>
        ) : (
          <Retain space={chosen} onKept={spaces.refresh} report={report} />
        )}
        <OpenSpace onOpened={spaces.refresh} report={report} />
      </div>
    </Card>
  );
}

// ── the page ──────────────────────────────────────────────────────────────

export function Memory() {
  const session = useSession();
  const [asked, setAsked] = useState<Question | null>(null);
  // Every asking is its own collection, even when the same question is asked
  // twice: the pages accumulated against the previous asking belong to it, and
  // continuing them from a cursor the new question never issued would answer
  // one question with another one's position.
  const [asking, setAsking] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  // What each page said it withheld, kept against the page that said it, so a
  // page read twice is counted once.
  const [disclosed, setDisclosed] = useState<Record<string, Disclosure>>({});
  /**
   * The two instants the answer in hand was reconstructed at.
   *
   * A continuation has to ask at the same two, and asking the *question* again
   * does not achieve that. Either instant may be left empty, and a partly
   * supplied pair is completed from a fresh `now` — so a second page that
   * repeated a question with only "as things stood" filled would arrive
   * carrying a `known_at` from a later instant than the cursor was pinned to,
   * and the platform refuses that rather than answering one question as
   * though it were another.
   *
   * So the moment is not worked out again here. It is the one the answer came
   * back stating, handed back by the contract for exactly this purpose.
   */
  const pinnedAt = useRef<{ as_of: string; known_at: string } | null>(null);

  const readPage = useCallback(
    async (cursor: string | null, signal: AbortSignal): Promise<MemoryPage> => {
      const answer = await api.get<MemoryResult>(MEMORY, {
        params: asked
          ? {
              ...paramsOf(asked),
              // The first page asks the question as it was asked, blanks and
              // all; every page after it asks at the moment that answer was
              // taken at. When there is somehow no such answer to quote, the
              // instants go unsent rather than guessed at, and the cursor's own
              // pinned moment stands.
              ...(cursor === null
                ? {}
                : {
                    cursor,
                    as_of: pinnedAt.current?.as_of,
                    known_at: pinnedAt.current?.known_at,
                  }),
            }
          : {},
        signal,
      });
      pinnedAt.current = { as_of: answer.as_of, known_at: answer.known_at };
      setDisclosed((seen) => ({
        // A read from the beginning is a new answer, not more of the old one.
        ...(cursor === null ? {} : seen),
        [cursor ?? ""]: {
          withheld: answer.withheld,
          view: answer.view,
          silent: answer.items.length === 0,
        },
      }));
      return asPage(answer);
    },
    [asked],
  );
  // Nothing is asked until somebody asks. `enabled` is what makes the landing
  // state a decision rather than a default.
  const pages = usePagedCollection<RetrievedMemory>(readPage, {
    enabled: asked !== null,
    resetKey: String(asking),
  });

  const record = pages.items.find((item) => item.record_id === selected) ?? null;

  // Two different absences, counted separately and worded separately.
  //
  // *Withheld* is authority: records that matched and this caller may not read.
  // Each page discloses it for its own window and the windows do not overlap,
  // so adding them double-counts nothing, and showing only the page in hand's
  // count would understate it the moment a second page arrived.
  //
  // What the addition does not produce is a total. A page that came back
  // showing nothing disclosed nothing — including that it withheld anything —
  // whatever it really withheld. So this is a floor over the pages that spoke,
  // and where one of them did not, it has to be told as a floor and the answer
  // cannot be called complete. The platform declines to say; the screen says
  // that rather than rounding it up into a number nobody gave it.
  const seen = Object.values(disclosed);
  const withheld = seen.reduce((total, page) => total + page.withheld, 0);
  // Alone, a page that showed nothing is the platform's own answer to a caller
  // who is seeing nothing, and it stands as given. It only becomes a gap once
  // it is composed with a page that did show something.
  const undisclosed = seen.length > 1 && seen.some((page) => page.silent);
  const view: MemoryView | "not known" = seen.some((page) => page.view === "partial")
    ? "partial"
    : undisclosed
      ? "not known"
      : "complete";

  if (!session.may("memory.read")) {
    return (
      <Card title="Memory">
        <Forbidden what="what this tenant retains" />
      </Card>
    );
  }

  return (
    <>
      <Banner title="Two questions, not one.">
        <strong>As things stood</strong> asks what was applicable at a moment.{" "}
        <strong>As Nexus knew it</strong> asks what the platform held at that moment. A correction
        written afterwards belongs to the second, never the first.
      </Banner>
      <Result outcome={outcome} />
      <div style={{ display: "grid", gap: 18 }}>
        <Spaces report={setOutcome} />

        <Ask
          onAsk={(question) => {
            setSelected(null);
            setAsked(question);
            setAsking((count) => count + 1);
          }}
        />

        {asked === null ? (
          <Card title="What is retained">
            <p className="nx-hint">
              Choose a subject, or ask across all of them, and Nexus will answer at the two moments
              you name.
            </p>
          </Card>
        ) : (
          <Card
            title="What is retained"
            actions={
              <button type="button" className="nx-button" onClick={pages.refresh}>
                Refresh
              </button>
            }
            bodyless
          >
            <Show resource={pages.state} what="what this tenant retains" onRetry={pages.refresh}>
              {(page) => {
                // The primitive types its state as the page shape every screen
                // shares; the answer's own disclosures came back on the same
                // object, and this is where that object was built.
                const answer = page as MemoryPage;
                return (
                  <>
                    <div className="nx-card__body" style={{ paddingBottom: 0 }}>
                      <Facts>
                        <Fact label="Scope">
                          {asked.scope === "all"
                            ? "Across all subjects"
                            : `${asked.subject} · ${asked.subjectId}`}
                        </Fact>
                        <Fact label="As things stood">{when(answer.as_of)}</Fact>
                        <Fact label="As Nexus knew it">{when(answer.known_at)}</Fact>
                        <Fact label="Answer">
                          <Pill tone={view === "complete" ? "ok" : "wait"}>{view}</Pill>
                        </Fact>
                      </Facts>
                      {undisclosed ? (
                        <p className="nx-hint" style={{ margin: "8px 0 0" }}>
                          A page of this answer came back showing nothing, and a page that shows
                          nothing says nothing about what it withheld. So the withheld count is a
                          floor, and this answer cannot be called complete.
                        </p>
                      ) : null}
                    </div>

                    {pages.items.length === 0 ? (
                      withheld > 0 ? (
                        // No rows. A row is a fact about existence at a position,
                        // and the count is deliberately all this can say.
                        <Empty
                          title={`${String(withheld)} records matched that you may not read.`}
                          hint="Their number is all that can be said about them."
                        />
                      ) : pages.hasMore ? (
                        // Nothing here, and the walk is not over. Saying "nothing
                        // matched" would end a query Nexus has not ended: a page
                        // whose rows were all withheld shows nothing and still
                        // carries the position to continue from.
                        <Empty
                          title="Nothing on the pages read so far."
                          hint="Nexus returned a position to continue from, so this is not the end of the answer. A page can come back empty and the walk still have somewhere to go."
                        />
                      ) : (
                        <Empty
                          title="No memory records matched this query."
                          hint="At these two moments, with these filters. Whether a space is open is a separate question, answered above — an empty space is exactly the one nothing has been retained in yet."
                        />
                      )
                    ) : (
                      <div className="nx-table-wrap">
                        <table className="nx-table">
                          <caption className="nx-visually-hidden">
                            Memory records matching this query
                          </caption>
                          <thead>
                            <tr>
                              <th scope="col">Retained</th>
                              <th scope="col">Standing</th>
                              <th scope="col">Class</th>
                              <th scope="col">Applicable</th>
                              <th scope="col">State</th>
                              <th scope="col" />
                            </tr>
                          </thead>
                          <tbody>
                            {pages.items.map((item) => (
                              <tr key={item.record_id}>
                                <td>
                                  <span className="nx-table__primary">{item.title}</span>
                                  <span
                                    className="nx-table__secondary"
                                    style={{ display: "block" }}
                                  >
                                    {item.retention_class.replace(/_/g, " ")} · {item.subject}{" "}
                                    <Identifier value={item.subject_id} />
                                  </span>
                                </td>
                                <td>
                                  <Pill tone={STANDING_TONE[item.epistemic_state]}>
                                    {item.epistemic_state}
                                  </Pill>
                                </td>
                                <td>
                                  <Classification value={item.classification} />
                                </td>
                                <td>
                                  {when(item.effective_from)}
                                  {item.valid_until ? ` until ${when(item.valid_until)}` : ""}
                                </td>
                                <td>
                                  <Pill tone={LIFECYCLE_TONE[item.lifecycle]}>{item.lifecycle}</Pill>
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="nx-button"
                                    aria-pressed={selected === item.record_id}
                                    onClick={() => {
                                      setSelected(item.record_id);
                                    }}
                                  >
                                    {selected === item.record_id ? "Showing" : "Show"}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {withheld > 0 && pages.items.length > 0 ? (
                      <p className="nx-hint" style={{ padding: "12px 18px" }}>
                        {/*
                          A lower bound for either of two reasons, because in
                          both it is one: the pages not yet read will disclose
                          their own, and a page that showed nothing disclosed
                          none of what it withheld. Stating it flat would be the
                          same silent truncation in a different sentence.
                        */}
                        <LowerBound
                          count={withheld}
                          hasMore={pages.hasMore || undisclosed}
                        />{" "}
                        further records
                        matched that you may not read. Their number is all that can be said about
                        them.
                      </p>
                    ) : null}

                    {/*
                      The other absence, kept apart from the one above. That one
                      is about authority — records that exist and are not for this
                      caller. This one is about position: the answer has not
                      finished being read. Two different claims about two
                      different things, and a screen that let them share a
                      sentence would be making neither of them.
                    */}
                    <More
                      collection={pages}
                      label="Show more records"
                      hint="This is one page of the answer, not all of it. Nexus returned a position to continue from, pinned to the two instants above, so reading on reconstructs the same past rather than a later one."
                      placement="bodyless"
                    />
                  </>
                );
              }}
            </Show>
          </Card>
        )}

        {record === null ? null : (
          <Card title={record.title}>
            <div style={{ display: "grid", gap: 16 }}>
              <Revision revisionId={record.revision_id} />
              <Revise record={record} onChanged={pages.refresh} report={setOutcome} />
              <Withdraw record={record} onChanged={pages.refresh} report={setOutcome} />
            </div>
          </Card>
        )}
      </div>
    </>
  );
}
